/**
 * App-wide date utilities.
 *
 * Two responsibilities, kept separate:
 *  1. PARSE / SORT - turn any supported date string into a stable timestamp
 *     for calendar-aware comparisons (never use lexical compare on day-first
 *     strings like "17-05-2026").
 *  2. DISPLAY - format dates using the user's chosen style (UK or USA),
 *     with a sensible default that follows the browser locale.
 *
 * Display style is sourced via a runtime setter so non-React code (sorts,
 * helpers) can read it without prop-drilling. React code should still set
 * it via the `useDateFormatPreference` hook.
 */

export type DateFormatPreference = 'uk' | 'usa';

let activePreference: DateFormatPreference | null = null;

export function setActiveDateFormat(pref: DateFormatPreference) {
  activePreference = pref;
}

export function getActiveDateFormat(): DateFormatPreference {
  if (activePreference) return activePreference;
  // Fallback: infer from browser. UK style for most of the world; USA for en-US.
  try {
    const lang = (navigator?.language || 'en-GB').toLowerCase();
    return lang === 'en-us' || lang.endsWith('-us') ? 'usa' : 'uk';
  } catch {
    return 'uk';
  }
}

// ============================================================
// PARSING - supports ISO and day-first / month-first strings
// ============================================================

/**
 * Parse a date string or Date into a local Date (no timezone shifts for
 * date-only strings). Returns null when input is invalid.
 *
 * Accepted shapes:
 *   - Date instance
 *   - "yyyy-MM-dd"               (ISO date-only)
 *   - "yyyy-MM-ddTHH:mm:ss..."   (ISO datetime - passed to Date)
 *   - "dd-MM-yyyy" / "dd/MM/yyyy"
 *   - "MM-dd-yyyy" / "MM/dd/yyyy" (only when the first part is > 12 it's
 *     unambiguous; otherwise interpreted by active preference)
 */
export function parseAppDate(input: Date | string | null | undefined): Date | null {
  if (input == null) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const s = String(input).trim();
  if (!s) return null;

  // ISO datetime - let Date handle it.
  if (s.includes('T')) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  // yyyy-MM-dd
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return makeLocalDate(+m[1], +m[2], +m[3]);

  // dd-MM-yyyy or dd/MM/yyyy or MM-dd-yyyy or MM/dd/yyyy
  m = /^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/.exec(s);
  if (m) {
    const a = +m[1];
    const b = +m[2];
    const year = +m[3];
    // Disambiguate
    if (a > 12 && b <= 12) return makeLocalDate(year, b, a); // clearly dd-MM
    if (b > 12 && a <= 12) return makeLocalDate(year, a, b); // clearly MM-dd
    // Ambiguous - fall back to active preference
    return getActiveDateFormat() === 'usa'
      ? makeLocalDate(year, a, b) // MM-dd-yyyy
      : makeLocalDate(year, b, a); // dd-MM-yyyy
  }

  // Last resort
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function makeLocalDate(year: number, month: number, day: number): Date | null {
  const d = new Date(year, month - 1, day);
  if (isNaN(d.getTime())) return null;
  return d;
}

/**
 * Sortable timestamp for any supported date input. Invalid dates sort last
 * for ascending and first for descending - callers usually want them
 * filtered upstream, but this avoids NaN poisoning the sort.
 */
export function dateSortKey(input: Date | string | null | undefined): number {
  const d = parseAppDate(input);
  return d ? d.getTime() : Number.NEGATIVE_INFINITY;
}

/** Compare two dates: newest first (descending). */
export function compareDateDesc(
  a: Date | string | null | undefined,
  b: Date | string | null | undefined,
): number {
  return dateSortKey(b) - dateSortKey(a);
}

/** Compare two dates: oldest first (ascending). */
export function compareDateAsc(
  a: Date | string | null | undefined,
  b: Date | string | null | undefined,
): number {
  return dateSortKey(a) - dateSortKey(b);
}

// ============================================================
// DISPLAY - UK / USA styles, English month names
// ============================================================

type DateStyle = 'short' | 'medium' | 'long' | 'with-time' | 'month-year' | 'time-only' | 'numeric';

/**
 * Format a date for display using the user's chosen format (UK or USA).
 * Output is always in English; only ordering / separators change.
 */
export function formatDate(
  date: Date | string | null | undefined,
  style: DateStyle = 'medium',
  prefOverride?: DateFormatPreference,
): string {
  const d = parseAppDate(date);
  if (!d) return date == null ? '' : String(date);

  const pref = prefOverride ?? getActiveDateFormat();

  if (style === 'time-only') {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  }

  if (style === 'month-year') {
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(d);
  }

  const day = d.getDate();
  const dd = String(day).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const monthShort = d.toLocaleString('en-US', { month: 'short' });
  const monthLong = d.toLocaleString('en-US', { month: 'long' });

  if (style === 'numeric') {
    return pref === 'usa' ? `${mm}/${dd}/${year}` : `${dd}/${mm}/${year}`;
  }

  // short / medium / long / with-time use English month names - order by pref
  const datePart = (() => {
    if (pref === 'usa') {
      if (style === 'short') return `${monthShort} ${day}`;
      if (style === 'long') return `${monthLong} ${day}, ${year}`;
      return `${monthShort} ${day}, ${year}`; // medium / with-time
    }
    // UK - day first
    if (style === 'short') return `${day} ${monthShort}`;
    if (style === 'long') return `${day} ${monthLong} ${year}`;
    return `${day} ${monthShort} ${year}`; // medium / with-time
  })();

  if (style === 'with-time') {
    const time = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
    return `${datePart}, ${time}`;
  }
  return datePart;
}
